/**
 * A value a commissioner set by hand, against what the rules say.
 *
 * `5d`'s argument, and the reason this exists rather than a plain editable field: **a keeper cap
 * is not a preference — it is what a passed rule says.** Editing it silently would let the app
 * disagree with its own rulebook, which is the thing the citation chips on every row exist to
 * prevent.
 *
 * SO THE EDIT IS NOT REFUSED, IT IS RECORDED. Refusing would be wrong: there are real reasons to
 * type a value directly — a transcription error in `config.ts`, a rule from 2014 nobody has
 * encoded, a correction the committee agreed in Discord and nobody minuted. What must not happen
 * is the edit becoming invisible. An override carries who made it, when, what the rules said, what
 * he set instead, and why — and the row it touches says so from then on.
 *
 * THE REASON IS REQUIRED. A log of unexplained changes is a log nobody reads, and the one moment
 * somebody knows why is the moment he makes the change.
 */

export interface Override {
  id: string;
  season: number;
  /** `freeKeepers`, `grid:16`, `luxuryTax:6` — what was written. */
  target: string;
  /** Human label for the row, so the log reads without decoding keys. */
  label: string;
  /** What the rules produced, kept so the deviation is auditable rather than merely noted. */
  ruleValue: string;
  /** What he set instead. */
  setTo: string;
  reason: string;
  by: string;
  at: string;
  /** The rule the value is governed by, for the citation on the log row. */
  cites?: string[];
  /** Cleared when somebody puts the value back. Kept, not deleted — the history is the point. */
  revertedAt?: string;
  revertedBy?: string;
}

export interface OverrideDraft {
  target: string;
  label: string;
  ruleValue: string;
  setTo: string;
  reason: string;
  cites?: string[];
}

export class OverrideError extends Error {}

/**
 * Build one, refusing the two cases that would make the log useless.
 *
 * A no-op override — the value already equals what the rules say — is not an override, and
 * logging one trains people to ignore the log. A reasonless one is worse: it records that
 * somebody did something and loses the only part a future reader needs.
 */
export function makeOverride(
  d: OverrideDraft, by: string, season: number, now: string, id = `${d.target}-${now}`,
): Override {
  if (d.setTo === d.ruleValue) {
    throw new OverrideError('That is already what the rules say — nothing to record.');
  }
  if (!d.reason.trim()) {
    throw new OverrideError('An override needs a reason. It is the only part anybody reads later.');
  }
  return {
    id,
    season,
    target: d.target,
    label: d.label,
    ruleValue: d.ruleValue,
    setTo: d.setTo,
    reason: d.reason.trim(),
    by: by.toUpperCase(),
    at: now,
    ...(d.cites?.length ? { cites: d.cites } : {}),
  };
}

/**
 * The overrides still in force, newest first.
 *
 * LATEST PER TARGET FIRST, THEN DROP THE REVERTED ONES — and the order matters. Filtering the
 * reverted rows out first would let an OLDER override on the same value come back into force the
 * moment somebody put the newer one back, which is the opposite of what "put it back" means: it
 * means the rules apply again, not that the previous hand-edit resumes.
 */
/**
 * How many rounds the farm auction runs, after any override.
 *
 * NOT ON `LeagueConfig`, and that is not an oversight — the farm rules are their own document and
 * `farmRounds` has always been an `ExtraKey`. What makes it worth a reader of its own is that it
 * is the one league value scheduled to change on a known date: two rounds is the 2025 experiment
 * and it lapses unless the committee re-votes it on 26 August. A commissioner needs to be able to
 * write the answer down that night without authoring a rule to do it, which is exactly what an
 * override is for.
 */
export const FARM_ROUNDS_TARGET = 'farmRounds';

export function farmRoundsNow(all: readonly Override[], fromRules: number): number {
  const o = inForce(all).find((x) => x.target === FARM_ROUNDS_TARGET);
  if (!o) return fromRules;
  const n = Number(o.setTo);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fromRules;
}

export function inForce(all: readonly Override[]): Override[] {
  const byTarget = new Map<string, Override>();
  for (const o of all) {
    const prev = byTarget.get(o.target);
    if (!prev || o.at > prev.at) byTarget.set(o.target, o);
  }
  return [...byTarget.values()]
    .filter((o) => !o.revertedAt)
    .sort((a, b) => b.at.localeCompare(a.at));
}

/** One line for the row it sits on: who moved it, away from what. */
export function overrideNote(o: Override): string {
  return `Set to ${o.setTo} by hand — the rules give ${o.ruleValue}.`;
}

/* -- a farm graduate who never made a 53 ------------------------------------------------------ */

/**
 * A commissioner recording that a farm graduate failed to make an opening-day 53-man roster.
 *
 * NOT A DEPARTURE FROM THE RULES — an earlier version of this was, and it was wrong. What the
 * rulebook actually says, read end to end:
 *
 *   2015-III-1  — he fails to make an opening 53, the GM forfeits one of his keeper spots, "though
 *                 you no longer have to carry the player".
 *   2019-II-5 §1 — tightens it: the spot is forfeited outright, he "cannot declare a keeper
 *                 'instead' or 'in place' of" him, AND the GM still pays luxury tax as if he had
 *                 kept him.
 *   2022-farm-system-clarification — voids 2017-I-5 §3, the clause that forced a GM to keep a
 *                 farm player opening the season on IR or PUP. So the forced-keep is gone for
 *                 that case too.
 *
 * So both halves are true at once and they are easy to conflate: **the slot is lost, and the
 * player does not have to be rostered.** The app briefly implemented "the slot is returned", which
 * is the one reading the rules never supported.
 *
 * WHY IT STILL GOES IN THE LOG. Not because it departs from anything — because the app cannot
 * know it. Whether the NFL cut a man is a fact only a human brings, it costs a GM a keeper slot
 * and real FAAB, and the reason for it should survive the argument in November. The log is the
 * ledger that already has a required reason, a name, a timestamp, and no delete.
 */
export const FARM_FORFEIT = 'farm-forfeit';

/** Why he is not on a roster. Both forfeit the slot; they cite different rules. */
export type ForfeitReason = 'cut' | 'ir-pup';

export const FORFEIT_REASONS: Readonly<Record<ForfeitReason, { label: string; cites: string[] }>> = {
  /*
   * CITE THE ACTIVE RULE FIRST. 2015-III-1 and 2019-II-5 are both superseded.
   * `2022-farm-system-keepers` is the one in force and re-states the whole penalty: forfeit the
   * spot, no obligation to roster him. 2019-II-5 stays on the `cut` case because §1 is where the
   * "taxed as though you kept him" wording actually lives.
   *
   * Never break an id across a line — `tests/ruleCitations.test.ts` reads these, and so does
   * anybody grepping for where a rule is used.
   */
  cut: {
    label: 'Failed to make an opening-day 53-man roster',
    cites: ['2022-farm-system-keepers', '2019-II-5'],
  },
  'ir-pup': {
    label: 'Opened the season on IR or PUP',
    cites: ['2022-farm-system-clarification', '2022-farm-system-keepers'],
  },
};

export const farmForfeitTarget = (gm: string, player: string): string =>
  `${FARM_FORFEIT}:${gm.toUpperCase()}:${player}`;

export function farmForfeitDraft(
  gm: string, player: string, why: ForfeitReason, reason: string,
): OverrideDraft {
  const r = FORFEIT_REASONS[why];
  return {
    target: farmForfeitTarget(gm, player),
    label: `${player} — farm graduate off the roster (${gm.toUpperCase()})`,
    ruleValue: 'mandatory keeper, carried on the roster',
    setTo: `${r.label} — slot forfeited, not rostered`,
    reason,
    cites: ['2013-III-1', ...r.cites],
  };
}

/** Every graduate currently forfeited, as `GM:Player` keys. Reverted rows do not count. */
export function forfeitedGraduates(all: readonly Override[]): Set<string> {
  const out = new Set<string>();
  for (const o of inForce(all)) {
    if (!o.target.startsWith(`${FARM_FORFEIT}:`)) continue;
    out.add(o.target.slice(FARM_FORFEIT.length + 1));
  }
  return out;
}

export const isForfeited = (forfeited: ReadonlySet<string>, gm: string, player: string): boolean =>
  forfeited.has(`${gm.toUpperCase()}:${player}`);

/* ------------------------------------------------------------- a tag used in a past season */

/**
 * A FRANCHISE TAG THE APP NEVER SAW.
 *
 * 2025 P05 §3 bars a player who has "previously been held under the franchise tag", so a tag used
 * in a past season removes him from the eligible pool for good. The app learns about the 2025 tags
 * from `data/franchise-tags.json`, which was reconstructed by reading 165 Discord messages —
 * because every one of those five declarations was made in Discord and none was written onto the
 * keeper sheet. That is the whole reason this rule was being broken silently.
 *
 * SO THE RECONSTRUCTION CANNOT BE THE ONLY CHANNEL. It is a file compiled into the build: when a
 * commissioner finds a sixth tag — and on 27 August Tim found two the file does not have — there
 * is nothing he can do about it from inside the app. Correcting the league's record should not
 * require a deploy, and the next time it does, the record will go missing the same way again.
 *
 * IT IS AN OVERRIDE RATHER THAN A FIELD, for the reason at the top of this file: it departs from
 * what the app's own data says, it costs somebody a keeper option, and the argument about it will
 * happen in a year's time. The log already has a required reason, a name, a timestamp and no
 * delete.
 *
 * KEYED BY PLAYER, NOT BY GM. §3 says "previously been held under the franchise tag" and stops
 * there — it is a fact about the player, not about the roster he was on. A tagged man who changes
 * hands is still a tagged man, and keying this by owner would hand his new GM a tag the rule does
 * not allow. That is the opposite of the farm forfeit above, which really is about one GM's slot.
 */
export const PRIOR_TAG = 'prior-tag';

export const priorTagTarget = (player: string): string => `${PRIOR_TAG}:${player}`;

export function priorTagDraft(player: string, season: number, reason: string): OverrideDraft {
  return {
    target: priorTagTarget(player),
    label: `${player} — held under the franchise tag in ${season}`,
    ruleValue: 'no franchise tag on record, so he is eligible',
    setTo: `franchised in ${season} — ineligible for good`,
    reason,
    cites: ['2025-proposal-05-franchise-tag'],
  };
}

/** Every player on record as previously franchised. Reverted rows do not count. */
export function priorlyFranchised(all: readonly Override[]): Set<string> {
  const out = new Set<string>();
  for (const o of inForce(all)) {
    if (!o.target.startsWith(`${PRIOR_TAG}:`)) continue;
    out.add(o.target.slice(PRIOR_TAG.length + 1));
  }
  return out;
}

/**
 * What to say on his row, in the words of the record rather than of the rule.
 *
 * "Already held under the franchise tag once" is what the RULE says and it is what every other
 * barred row says. A player barred by a commissioner's correction deserves the extra clause — WHO
 * says so and WHEN — because that is the sentence somebody will argue with, and pointing him at
 * the override log is how the argument gets settled rather than repeated.
 */
export function priorTagReason(all: readonly Override[], player: string): string | null {
  const row = inForce(all).find((o) => o.target === priorTagTarget(player));
  if (!row) return null;
  return `${row.setTo.charAt(0).toUpperCase()}${row.setTo.slice(1)}, recorded by `
    + `${row.by.charAt(0)}${row.by.slice(1).toLowerCase()} (2025 P05 §3).`;
}
